import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { FormikHelpers, useFormik } from 'formik';

import { API, ResponseStatus } from 'app/api';
import { URL } from 'app/api/constants';
import { FormControl } from 'app/design-system/components/FormControl';
import { formatPhoneNumber } from 'app/design-system/components/PhoneInput/phone-input-helpers';
import { StepperContext } from 'app/design-system/components/Stepper/context';
import { Trans } from 'app/lang';
import type { CodeFormValue } from 'app/widgets/CodeConfirmation';
import {
  CODE_FORM_VALUES,
  CODE_LENGTH,
  CodeConfirmation,
  CodeError,
} from 'app/widgets/CodeConfirmation';

import { CHECK_ICON_TIME_DURATION } from '../../../../constants';
import { RegContext } from '../../../../context';

import dictionary from './SecondStepForm.dictionary.json';

import styles from '../../../../Signing.module.scss';

const NOT_NUMBER = /\D/g;

const SecondStepForm: FC = () => {
  const { activeStep, backwardStep, forwardStep } = useContext(StepperContext);
  const { regData, setRegData } = useContext(RegContext);

  const [error, setError] = useState<CodeError | null>(regData.codeError);

  const { sessionToken, phoneToken, hasAccount, requisite, disabled } = regData;

  useEffect(() => {
    setRegData((prevState) => ({ ...prevState, regStep: activeStep }));
  }, []);

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setTouched,
    setFieldValue,
  } = useFormik<CodeFormValue>({
    initialValues: CODE_FORM_VALUES,
    onSubmit: async (values: CodeFormValue, formikHelpers: FormikHelpers<CodeFormValue>) => {
      const { setSubmitting } = formikHelpers;
      const { code } = values;

      if (code.length < CODE_LENGTH) {
        setError(CodeError.NOT_CODE);
        return setSubmitting(false);
      }

      if (sessionToken === null || phoneToken === null) {
        setError(CodeError.NOT_TOKEN);
        return setSubmitting(false);
      }

      setRegData((prevState) => ({ ...prevState, disabled: true }));

      API.registration.sendCheckCode({ phoneNumber: regData.requisite, code })
        .then((response) => {
          if (response.status === ResponseStatus.SUCCESS) {
            setError(null);
            setRegData((prevState) => ({ ...prevState, checked: true }));

            setTimeout(() => {
              forwardStep();
              setRegData((prevState) => ({ ...prevState, checked: false }));
            }, CHECK_ICON_TIME_DURATION);
          }
          else if (response.status === ResponseStatus.IS_REGISTERED) {
            setRegData((prevState) => ({ ...prevState, hasAccount: true }));
          }
          else if (response.status === ResponseStatus.UNPROCESSABLE_ENTITY) {
            response.data.type.endsWith('secure-code-token-not-found')
              ? setError(CodeError.NOT_TOKEN)
              : setError(CodeError.NOT_CODE);
          }
          else {
            setError(CodeError.SOMETHING_WRONG);
          }
        })
        .finally(() => setRegData((prevState) => ({ ...prevState, disabled: false })));
    },
  });

  useEffect(() => {
    setFieldValue('code', values.code.replace(NOT_NUMBER, ''));
    if (values.code.length === CODE_LENGTH) {
      handleSubmit();
    }
    if (!!errors.code && touched.code) {
      setError(CodeError.NOT_CODE);
    }
  }, [values.code]);

  const changeErrorState = useCallback(() => {
    setError(null);
  }, []);

  const changeVerifyDataHandler = useCallback(() => {
    setRegData((prevState) => ({ ...prevState, codeError: null }));
    backwardStep();
  }, [backwardStep]);

  if (hasAccount) {
    return (
      <div className={styles.infoText}>
        <span>
          <Trans data={dictionary.hasAccountInfo}
                 tOptions={{ phoneNumber: formatPhoneNumber(requisite) }}
                 components={{
                   wrapper: (
                     <p />
                   ),
                 }} />
        </span>
      </div>
    );
  }

  const reSendData = {
    url: URL.REGISTRATION_CHECK_PHONE,
    data: { phoneNumber: requisite },
  };

  return (
    <form id={`registrationFormStep_${activeStep}`}
          onSubmit={handleSubmit}
          data-test={`registrationFormStep_${activeStep}`}>
      <FormControl>
        <CodeConfirmation value={values.code}
                          data={regData}
                          changeData={setRegData}
                          reSendData={reSendData}
                          error={error}
                          changeErrorState={changeErrorState}
                          disabled={disabled}
                          changeVerifyData={changeVerifyDataHandler}
                          formikHelpers={{ setFieldValue, setTouched }} />
      </FormControl>
    </form>
  );
};

export default SecondStepForm;
